import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiClient } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import type {
  AIHomeworkBlock,
  AIHomeworkResponse,
  AIHomeworkService,
  HomeworkChatParams,
  PracticeQuestionParsed,
  PracticeQuestionResponse,
  HintResponse,
  VoteParams,
  VoteResult,
} from './aiHomeworkTypes';

const HOMEWORK_HINT = 1;
const HOMEWORK_PRACTICE_QUESTION = 2;

interface RawMessageBlock {
  html?: string | null;
  type?: string | number | null;
}

interface RawPracticeProblem {
  answer?: string | null;
  answer_index?: number | null;
  answerIndex?: number | null;
  options?: (string | null)[] | null;
  text?: string | null;
}

interface RawGetChatResponse {
  blocks?: (RawMessageBlock | null)[] | null;
  body?: string | null;
  current_response?: string | null;
  currentResponse?: string | null;
  display_message?: string | null;
  displayMessage?: string | null;
  downvotes?: number | string | null;
  error?: string | null;
  id?: string | null;
  model?: string | null;
  practice_problem?: RawPracticeProblem | null;
  practiceProblem?: RawPracticeProblem | null;
  request_message?: string | null;
  requestMessage?: string | null;
  upvotes?: number | string | null;
}

interface RawUpdateChatResponse {
  current_response?: string | null;
  currentResponse?: string | null;
  new_response?: RawGetChatResponse | null;
  newResponse?: RawGetChatResponse | null;
  response?: RawGetChatResponse | null;
}

interface GetChatRequest {
  component_id: string;
  course: {
    id?: string;
    parent_subject?: string;
  };
  iteration?: number;
  question_uid: string;
  type: number;
}

interface UpdateChatRequest {
  new_response: {
    current_response: string;
    downvotes: number;
    id: string;
    model: string;
    request_message: string;
    upvotes: number;
  };
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function decodeBasicHtmlEntities(value: string) {
  return value.replace(
    /&(#x?[0-9a-f]+|[a-z]+);/gi,
    (entity, code: string) => {
      const normalizedCode = code.toLowerCase();

      if (normalizedCode.startsWith('#x')) {
        const parsed = Number.parseInt(normalizedCode.slice(2), 16);
        return Number.isFinite(parsed) ? String.fromCodePoint(parsed) : entity;
      }

      if (normalizedCode.startsWith('#')) {
        const parsed = Number.parseInt(normalizedCode.slice(1), 10);
        return Number.isFinite(parsed) ? String.fromCodePoint(parsed) : entity;
      }

      const namedEntities: Record<string, string> = {
        amp: '&',
        gt: '>',
        lt: '<',
        nbsp: ' ',
        quot: '"',
      };

      return namedEntities[normalizedCode] ?? entity;
    },
  );
}

function normalizePracticeQuestionText(value: unknown): string | null {
  const text = normalizeString(value);

  if (!text) {
    return null;
  }

  if (!/<[a-z][\s\S]*>/i.test(text)) {
    return text;
  }

  return normalizeString(
    decodeBasicHtmlEntities(text)
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\s*\/?(?:p|div|li|h[1-6]|tr)[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n'),
  );
}

function normalizeNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return 0;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getResponseContent(response: RawGetChatResponse) {
  return (
    normalizeString(response.current_response ?? response.currentResponse) ??
    normalizeString(response.body) ??
    ''
  );
}

function getResponseError(response: RawGetChatResponse) {
  return normalizeString(response.error);
}

export function normalizeAIHomeworkResponse(
  response: RawGetChatResponse,
  fallbackRequestMessage = '',
): AIHomeworkResponse {
  const responseError = getResponseError(response);

  if (responseError) {
    throw new Error(responseError);
  }

  const currentResponse = getResponseContent(response);
  const id = normalizeString(response.id);

  if (!id || !currentResponse) {
    throw new Error('AI response was incomplete. Please try again.');
  }

  return {
    blocks: normalizeAIHomeworkBlocks(response),
    currentResponse,
    displayMessage:
      normalizeString(response.display_message ?? response.displayMessage) ??
      undefined,
    downvotes: normalizeNumber(response.downvotes),
    id,
    model: normalizeString(response.model) ?? '',
    requestMessage:
      normalizeString(response.request_message ?? response.requestMessage) ??
      fallbackRequestMessage,
    upvotes: normalizeNumber(response.upvotes),
  };
}

/**
 * Reads the structured content blocks off a response.
 *
 * Falls back to wrapping currentResponse when a response carries none — that
 * covers cached entries written before this change, and the server's own
 * degrade-rather-than-fail path.
 */
export function normalizeAIHomeworkBlocks(
  response: RawGetChatResponse,
): AIHomeworkBlock[] {
  const raw = response.blocks ?? [];
  const blocks = (Array.isArray(raw) ? raw : [])
    .map((block) => normalizeString(block?.html) ?? '')
    .filter((html) => html.length > 0)
    .map((html) => ({ html }));

  if (blocks.length > 0) {
    return blocks;
  }

  const fallback = getResponseContent(response);
  return fallback ? [{ html: fallback }] : [];
}

/**
 * Reads the practice problem off a response.
 *
 * The previous implementation parsed JSON out of the response text and stripped
 * markdown fences the model had been instructed not to emit. The server now
 * requests a response schema, so the fields arrive typed and neither step is
 * needed.
 */
export function normalizePracticeProblem(
  response: RawGetChatResponse,
): PracticeQuestionParsed {
  const raw = response.practice_problem ?? response.practiceProblem;

  if (!raw) {
    throw new Error('The practice problem response was incomplete. Please try again.');
  }

  const text = normalizeString(raw.text) ?? '';
  if (!text) {
    throw new Error('The practice problem response was incomplete. Please try again.');
  }

  const options = (Array.isArray(raw.options) ? raw.options : [])
    .map((option) => normalizeString(option) ?? '')
    .filter((option) => option.length > 0);

  const rawIndex = raw.answer_index ?? raw.answerIndex;
  // A negative index is the schema's "this question has no options" sentinel;
  // treating it as an index would make the reveal path look up options[-1].
  const answerIndex =
    typeof rawIndex === 'number' && Number.isFinite(rawIndex) && rawIndex >= 0 && options.length > 0
      ? rawIndex
      : undefined;

  return {
    answerIndex,
    answerText: normalizeString(raw.answer) ?? undefined,
    options,
    text,
  };
}

function getUpdatedRawResponse(
  response: RawUpdateChatResponse | RawGetChatResponse,
): RawGetChatResponse {
  if ('new_response' in response || 'newResponse' in response || 'response' in response) {
    return (
      (response as RawUpdateChatResponse).new_response ??
      (response as RawUpdateChatResponse).newResponse ??
      (response as RawUpdateChatResponse).response ??
      response
    );
  }

  return response;
}

export class KuduAIHomeworkService implements AIHomeworkService {
  constructor(private readonly apiClient: ApiClient) {}

  async getHint(params: HomeworkChatParams): Promise<HintResponse> {
    const response = await this.apiClient.invokeKuduMethod<
      RawGetChatResponse,
      GetChatRequest
    >('GetChatV2', {
      component_id: params.componentId,
      course: {
        ...(params.course.id ? { id: params.course.id } : {}),
        ...(params.course.parentSubject
          ? { parent_subject: params.course.parentSubject }
          : {}),
      },
      // Question identity only. The prompt is assembled server-side, so a
      // client can no longer send arbitrary text to the model.
      question_uid: params.questionUid,
      type: HOMEWORK_HINT,
    });

    return normalizeAIHomeworkResponse(response);
  }

  async getPracticeQuestion(
    params: HomeworkChatParams,
  ): Promise<PracticeQuestionResponse> {
    const response = await this.apiClient.invokeKuduMethod<
      RawGetChatResponse,
      GetChatRequest
    >('GetChatV2', {
      component_id: params.componentId,
      course: {
        ...(params.course.id ? { id: params.course.id } : {}),
        ...(params.course.parentSubject
          ? { parent_subject: params.course.parentSubject }
          : {}),
      },
      iteration: params.iteration ?? 0,
      question_uid: params.questionUid,
      type: HOMEWORK_PRACTICE_QUESTION,
    });
    const normalized = normalizeAIHomeworkResponse(response);

    return {
      ...normalized,
      question: normalizePracticeProblem(response),
    };
  }

  async submitVote({ response, vote }: VoteParams): Promise<VoteResult> {
    const nextResponse = {
      ...response,
      downvotes: vote === 'down' ? response.downvotes + 1 : response.downvotes,
      upvotes: vote === 'up' ? response.upvotes + 1 : response.upvotes,
    };

    const rawResponse = await this.apiClient.invokeKuduMethod<
      RawUpdateChatResponse | RawGetChatResponse,
      UpdateChatRequest
    >('UpdateChatV2', {
      new_response: {
        current_response: nextResponse.currentResponse,
        downvotes: nextResponse.downvotes,
        id: nextResponse.id,
        model: nextResponse.model,
        request_message: nextResponse.requestMessage,
        upvotes: nextResponse.upvotes,
      },
    });
    const updatedRawResponse = getUpdatedRawResponse(rawResponse);
    const hasReplacementContent = Boolean(
      normalizeString(
        updatedRawResponse.current_response ?? updatedRawResponse.currentResponse,
      ),
    );
    const normalized = hasReplacementContent
      ? normalizeAIHomeworkResponse(updatedRawResponse, nextResponse.requestMessage)
      : nextResponse;

    return {
      replacedContent:
        normalized.currentResponse !== response.currentResponse ||
        normalized.id !== response.id,
      response: normalized,
    };
  }
}

export function useKuduAIHomeworkService(): AIHomeworkService | null {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });

    return new KuduAIHomeworkService(client);
  }, [getIdToken, location.hash, location.pathname, location.search, navigate, user]);
}
